import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Popover } from '@barges/react';
import { updateTcpBanner } from '@common/api/api';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import classes from './classes.module.scss';
import { GDPRConsernPopupThemes } from '../../../config/themes';
import { appLogo } from '../../../logo/logo';
import env from '../../../config/variables';
import { parseAndTrackAnalyticEvents } from '../../../actions/analytics';
import { useNotification } from '../../firebase_messaging_provider';
import { addParamsToAnalitycs } from '../../../actions/application';

const {
  TCP_BANNER
} = env;

const termsAndPoliciesUpdateViewedEvent = (tcpBanner, termsAndPpUpdateViewed, termsAndPoliciesUpdateViewed) => {
  if (tcpBanner && termsAndPpUpdateViewed && termsAndPoliciesUpdateViewed) {
    parseAndTrackAnalyticEvents({ termsAndPoliciesUpdateViewed });
  }
};

const handleLinkClick = (url, setPrivacyPolicyClicked, setTermsOfUseClicked) => {
  if (url?.includes('privacy')) {
    setPrivacyPolicyClicked(true);
  } else setTermsOfUseClicked(true);
};

function GDPRConsernPopup({
  GDPRConsernPopupVisible,
  gdprSwitcherValueChanged,
  shouldShowGDPRConsent,
  theme,
  socialParams,
  authSocial,
  clearError,
  tcpBanner,
  onRegisterClicked,
  termsAndPoliciesUpdateViewed
}) {
  const [termsOfUseClicked, setTermsOfUseClicked] = useState(false);
  const [privacyPolicyClicked, setPrivacyPolicyClicked] = useState(false);
  const [termsAndPpUpdateViewed, setTermsAndPpUpdateViewed] = useState(false);
  const { requestPermission } = useNotification();

  const hideGDPRConsernPopup = () => {
    shouldShowGDPRConsent({ [theme]: false });
  };

  useEffect(() => {
    if (tcpBanner) {
      shouldShowGDPRConsent({ [GDPRConsernPopupThemes.server]: true });
      setTermsAndPpUpdateViewed(true);
    }
  }, [tcpBanner]);

  useEffect(() => {
    termsAndPoliciesUpdateViewedEvent(tcpBanner, termsAndPpUpdateViewed, termsAndPoliciesUpdateViewed);
  }, [termsAndPpUpdateViewed]);

  const serverContentBtnOnClick  = () => {
    window.store.dispatch(addParamsToAnalitycs({
      [ANALYTICS_EVENTS.TERMS_AND_POLICIES_UPDATE_CLICKED]: {
        'terms of use clicked': termsOfUseClicked,
        'privacy policy clicked': privacyPolicyClicked
      }
    }));
    updateTcpBanner().then(() => {});
    hideGDPRConsernPopup();
    gdprSwitcherValueChanged(true);
  };

  const regularContentBtnOnClick = () => {
    gdprSwitcherValueChanged(true);
    onRegisterClicked();
    hideGDPRConsernPopup();
  };

  const socialContinueOnClick = () => {
    clearError();
    gdprSwitcherValueChanged(true);
    requestPermission(true);
    authSocial(socialParams.socRequest, socialParams.signupFlow);
    hideGDPRConsernPopup();
  };

  const serverContentLinkOnClick = (href) => {
    if (href) {
      handleLinkClick(href, setPrivacyPolicyClicked, setTermsOfUseClicked);
    }
  };

  const renderServerContent = () => (
    <div data-testid="gdpr-server-content" className={ classes.ppTofModalSignUpContent }>
      <img src={ appLogo() } width={ 60 } height={ 60 } alt="" />
      <div className={ classes.ppTofModalSignUpHeader }>
        {tcpBanner?.header || ''}
      </div>
      <div className={ classes.tcpBannerText }>
        <ReactMarkdown
          components={ {
            a: ({
              href, children, ...props
            }) => (
              <a
                href={ href }
                target="_blank"
                rel="noopener noreferrer"
                { ...props }
                onClick={ () => serverContentLinkOnClick(href) }
              >
                {children}
              </a>
            )
          } }
        >
          {tcpBanner?.body || ''}
        </ReactMarkdown>
      </div>
      <div className={ classes.tcpBannerText }>
        <ReactMarkdown>{tcpBanner?.description || ''}</ReactMarkdown>
      </div>
      <div>
        <button
          type="button"
          data-testid="gdpr-server-content-button"
          onClick={ serverContentBtnOnClick }
          className={ classes.ppTofModalSignUpButton }
        >
          {tcpBanner?.button || ''}
        </button>
      </div>
    </div>
  );

  const renderRegularContent = () => (
    <div data-testid="gdpr-regular-content" className={ classes.ppTofModalSignUpContent }>
      <img src={ appLogo() } width={ 60 } height={ 60 } alt="" />
      <div className={ classes.ppTofModalSignUpHeader }>
        <ReactMarkdown>{TCP_BANNER.header}</ReactMarkdown>
      </div>
      <div>
        <div className={ classes.tcpBannerBodyText }>
          <ReactMarkdown
            components={ {
              a: ({
                href, children, ...props
              }) => (
                <a
                  href={ href }
                  target="_blank"
                  rel="noopener noreferrer"
                  { ...props }
                >
                  {children}
                </a>
              )
            } }
          >
            {TCP_BANNER.body || ''}
          </ReactMarkdown>
        </div>
      </div>
      <div>
        <ReactMarkdown>{TCP_BANNER.description || ''}</ReactMarkdown>
      </div>
      <div>
        <button
          type="button"
          data-testid="gdpr-regular-content-button"
          onClick={ theme === GDPRConsernPopupThemes.social ? socialContinueOnClick : regularContentBtnOnClick }
          className={ classes.ppTofModalSignUpButton }
        >
          {TCP_BANNER.button}
        </button>
        <button
          type="button"
          data-testid="gdpr-regular-content-cancel-button"
          onClick={ hideGDPRConsernPopup }
          className={ classes.ppTofModalCancelButton }
        >
          {TCP_BANNER.cancel}
        </button>
      </div>
    </div>
  );

  const renderModalContent = () => {
    if (theme === GDPRConsernPopupThemes.server) return renderServerContent();
    return renderRegularContent();
  };

  const renderGDPRConsernPopup = () => (
    <Popover
      isOpen={ (GDPRConsernPopupVisible && GDPRConsernPopupVisible[theme]) || false }
      centered
      className="gdpr-consern-popup"
    >
      {renderModalContent()}
    </Popover>
  );

  return renderGDPRConsernPopup();
}

export default GDPRConsernPopup;
